import { useEffect, useState } from "react";
import { getVisureUsage } from "../../api/license-usage";
import type { OrgVisureSummary } from "../../../api/license-usage-summary";
import { Btn } from "../../ui";

export function VisureQuota({ refreshKey = 0, selectedCount = 0 }: { refreshKey?: number; selectedCount?: number }) {
  const [summary, setSummary] = useState<OrgVisureSummary | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let controller: AbortController | null = null;
    const load = async () => {
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      try {
        const result = await getVisureUsage(signal);
        if (cancelled || signal.aborted) return;
        setSummary(result);
        setError(false);
      } catch {
        if (!cancelled && !signal.aborted) {
          setSummary(null);
          setError(true);
        }
      }
    };
    void load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 30_000);
    return () => { cancelled = true; controller?.abort(); clearInterval(timer); };
  }, [refreshKey, retry]);

  if (error) return <div className="preview-box" role="status">
    Disponibilità visure non disponibile. <Btn size="sm" variant="ghost" onClick={() => setRetry(value => value + 1)}>Riprova</Btn>
  </div>;
  if (!summary) return <div className="muted" role="status">Caricamento disponibilità visure…</div>;

  const reset = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Rome" }).format(new Date(summary.resetsAt));
  const overLimit = summary.remaining !== null && selectedCount > summary.remaining;
  return <section className="preview-box" aria-label="Disponibilità visure" style={{ fontSize: 12, lineHeight: 1.6 }}>
    <div className="row gap-12" style={{ flexWrap: "wrap" }}>
      <strong>{summary.remaining === null ? "Visure · Nessun limite mensile" : `Visure disponibili questo mese: ${summary.remaining}`}</strong>
      <span>{summary.usage.monthly} inoltrate · {summary.usage.active} ricerche in corso</span>
    </div>
    {summary.license.maxVisurePerMonth !== null && <div className="muted">Limite mensile: {summary.license.maxVisurePerMonth} · Il conteggio riparte il {reset}.</div>}
    {summary.blockedReason && <div role="status" style={{ color: "var(--warn)", marginTop: 4 }}>{summary.blockedReason}</div>}
    {overLimit && <div role="status" style={{ marginTop: 4 }}>
      Hai selezionato {selectedCount} persone: al momento possono partire al massimo {summary.remaining} nuove visure questo mese. Le altre ricerche resteranno in coda.
    </div>}
  </section>;
}
